import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { API } from "../config";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, iva: 0, total: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCart = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (user) fetchCart();
    else setCart({ items: [], subtotal: 0, iva: 0, total: 0 });
  }, [user, fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1, packType = "individual") => {
    if (!token) return null;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/cart/add`, {
        product_id: productId,
        quantity,
        pack_type: packType
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
      setCartOpen(true);
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const removeFromCart = useCallback(async (productId, packType = "individual") => {
    if (!token) return;
    try {
      const res = await axios.delete(`${API}/cart/item/${productId}?pack_type=${packType}`, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
    } catch { /* silent */ }
  }, [token]);

  const clearCart = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.delete(`${API}/cart/clear`, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
    } catch { /* silent */ }
  }, [token]);

  const checkout = useCallback(async () => {
    if (!token) return null;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/cart/checkout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, cartOpen, setCartOpen, addToCart, removeFromCart, clearCart, checkout, loading, itemCount, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};
